/**
 * R232q43: Sirus M2-extracted creature lookup table.
 *
 * The VPS at /srv/storm-games/aiwari/models/sirus/creature/<name>/<name>.glb
 * holds 1800+ creature meshes. The server emits generic mob.kind values like
 * 'beast', 'humanoid', 'undead', plus zone-prefixed kinds like
 * 'redrock_goblin'. This module maps those to actual on-disk Sirus folder
 * names so {@link GlbMob} can HEAD-probe the right URL chain before falling
 * back to Quaternius/Sketchfab GLBs.
 *
 * Coverage strategy:
 *   1. Direct kind → Sirus folder map (50+ entries) covering every creature
 *      type emitted by zone-spawns.ts + boss-scripts.ts + dungeons.
 *   2. Strip zone prefixes ("redrock_", "tirisfal_", …) and re-lookup.
 *   3. Token fuzzy match for compound kinds (lichking, kelthuzad, illidan).
 *   4. null when no match → caller falls back to its existing path chain.
 *
 * Each entry returns an ordered list of candidates: the loader tries them
 * top-to-bottom and uses the first that HEAD-probes 200 + binary
 * content-type. This way we can ship "best-guess first, fallback second"
 * without hard-failing if a candidate goes missing.
 */

const SIRUS_PREFIX = 'sirus/creature/';

/** Build a relative path: kind 'wolf' → 'sirus/creature/wolf/wolf.glb'. */
function s(name: string): string {
  return `${SIRUS_PREFIX}${name}/${name}.glb`;
}

/**
 * R232q43: kind → Sirus path candidates. Each kind maps to an ordered list;
 * the first to HEAD-probe 200 wins. Order = "most representative first".
 *
 * Names are the actual on-disk folder names from
 * /srv/storm-games/aiwari/models/sirus/creature/.
 */
export const SIRUS_CREATURE_PATHS: Record<string, string[]> = {
  // --- Animals / beasts (server-emitted generic kinds) ---
  bat:           [s('bat'), s('felbat')],
  bear:          [s('bear'), s('bear2'), s('bearice'), s('bearprimal')],
  beast:         [s('bear'), s('worg'), s('giantbeast')],
  boar:          [s('boar'), s('boar2'), s('felboar')],
  cat:           [s('cat'), s('cat2'), s('cat2large'), s('saber2')],
  crab:          [s('crab'), s('crab2'), s('hermitcrab')],
  critter:       [s('rabbit'), s('squirrel'), s('rat')],
  crocolisk:     [s('crocodile'), s('cataclysmcrocodile')],
  hyena:         [s('hyena'), s('hyena2')],
  owl:           [s('owl'), s('owl2'), s('owlcat')],
  raptor:        [s('raptor'), s('raptor2'), s('raptoroutland'), s('armoredraptor')],
  scorpid:       [s('scorpion')],
  scorpion:      [s('scorpion')],
  spider:        [s('spider'), s('giantspider'), s('icespider')],
  tallstrider:   [s('tallstrider'), s('tallstrider2'), s('tallstriderprimal')],
  totem:         [s('earthelementalprimal'), s('stoneconstruct')],
  turtle:        [s('turtle'), s('seaturtle'), s('dragonturtle')],
  'wind-serpent':[s('windserpent'), s('windserpentoutland'), s('serpent')],
  'carrion-bird':[s('carrionbird'), s('carrionbirdoutland'), s('vulture')],

  // --- Canines ---
  wolf:          [s('wolf'), s('direwolf'), s('wolfdraenor')],
  worg:          [s('worg'), s('direwolf'), s('saberworg')],
  hellhound:     [s('hellhound'), s('hellhoundskeleton')],
  felhound:      [s('felhound'), s('felhound2')],

  // --- Humanoids (generic) ---
  humanoid:      [s('humanmalewarriormedium'), s('humanmalewarriorlight')],
  humanoid_male: [s('humanmalewarriormedium'), s('humanmalewarriorlight')],
  humanoid_female: [s('humanfemalewarriormedium'), s('humanfemalewarriorlight')],
  bandit:        [s('humanmalebanditmedium'), s('humanmalebanditlight'), s('humanthief')],
  guard:         [s('humanmaleguard'), s('humanmalewarriorheavy')],
  knight:        [s('humanmalewarriorheavy'), s('humanmalemarshal')],
  noble:         [s('humanmalenoble'), s('humanmalemarshal')],
  peasant:       [s('humanmalepeasant'), s('humanmalefarmer'), s('humanfemalepeasant')],
  pirate:        [s('humanmalepiratecrewman'), s('humanmalepirateswashbuckler'), s('humanmalepiratecaptain')],
  captain:       [s('humanmalepiratecaptain'), s('humanmalemarshal')],
  blacksmith:    [s('humanmaleblacksmith'), s('humanfemaleblacksmith')],
  caster:        [s('humanmalecaster'), s('humanfemalecaster'), s('humanmalewizard')],
  wizard:        [s('humanmalewizard'), s('humanmalecaster')],

  // --- Faction races ---
  goblin:        [s('goblin'), s('hobgoblin'), s('gilgoblin')],
  gnoll:         [s('gnoll'), s('gnollmelee'), s('gnollcaster')],
  kobold:        [s('kobold'), s('kobold2')],
  troll:         [s('troll'), s('foresttroll'), s('trolldire')],
  tribal:        [s('orcmalewarriorlight'), s('foresttroll'), s('troll')],
  orc:           [s('orcmalewarriormedium'), s('orcmalewarriorheavy'), s('orcmalewarriorlight')],
  dwarf:         [s('earthendwarf'), s('irondwarf'), s('dwarfmalewarriorlight')],
  furbolg:       [s('furbolg'), s('direfurbolg'), s('bogbeast')],
  centaur:       [s('centaur')],
  harpy:         [s('harpy'), s('harpy2')],
  naga:          [s('nagamale'), s('nagafemale'), s('nagabrute'), s('nagalordmale')],
  satyr:         [s('satyr'), s('satyr2'), s('satyr3')],
  murloc:        [s('murloc'), s('murloc2')],
  quillboar:     [s('quillboar'), s('quillboar2')],
  ogre:          [s('ogre'), s('ogre02'), s('ogremage02')],
  ettin:         [s('ettin')],
  vrykul:        [s('vrykulfemale'), s('frostvrykulmale')],
  kvaldir:       [s('kvaldir')],
  arakkoa:       [s('arakkoa'), s('arakkoagolem')],

  // --- Undead ---
  undead:        [s('zombie'), s('ghoul'), s('skeleton')],
  zombie:        [s('zombie'), s('zombie2'), s('zombieburnt')],
  ghoul:         [s('ghoul'), s('ghoul2')],
  skeleton:      [s('skeleton'), s('skeletalwarrior'), s('skeletonmage')],
  skeleton_warrior: [s('skeletalwarrior'), s('skeleton')],
  skeleton_mage: [s('skeletonmage'), s('skeleton')],
  banshee:       [s('banshee'), s('banshee2')],
  wraith:        [s('wraith'), s('wraith2'), s('ghost')],
  ghost:         [s('ghost'), s('ghost2'), s('ghostelemental')],
  geist:         [s('geist'), s('northrendgeist')],
  abomination:   [s('abominationlord'), s('abominationsmall'), s('boneabomination')],
  lich:          [s('lich'), s('lich2'), s('lichlord')],
  necromancer:   [s('necromancer'), s('necromancer2')],
  death_knight:  [s('mounteddeathknight'), s('skeletalwarhorse')],
  vampire:       [s('vampyrbloodprince'), s('ashvampire')],
  nerubian:      [s('nerubian'), s('nerubianwarrior'), s('nerubiancaster')],

  // --- Demons ---
  demon:         [s('felguard'), s('infernal'), s('felhound')],
  imp:           [s('imp'), s('imp2'), s('impmother'), s('impoutland')],
  felguard:      [s('felguard'), s('felguard2')],
  succubus:      [s('succubus'), s('succubus2')],
  voidwalker:    [s('voidwalker'), s('voidwalker2'), s('voidwalkeroutland')],
  doomguard:     [s('doomguard'), s('doomguard2'), s('doomguardoutland')],
  infernal:      [s('infernal'), s('infernal2')],
  pitlord:       [s('pitlord'), s('pitlord2'), s('pitinfernal')],
  dreadlord:     [s('dreadlord'), s('dreadlord2'), s('dreadlordfel2')],
  eredar:        [s('eredar'), s('eredarbrutemale'), s('eredarfemale')],
  wrathguard:    [s('wrathguard'), s('wrathguard2')],

  // --- Elementals ---
  elemental:     [s('earthelementalprimal'), s('fireelemental'), s('waterelemental')],
  elemental_fire:  [s('fireelemental'), s('fireelemental2')],
  elemental_water: [s('waterelemental'), s('waterelemental2')],
  elemental_earth: [s('earthelementalprimal'), s('elementalearth')],
  elemental_air:   [s('airelemental'), s('airelemental2')],

  // --- Dragonkin ---
  dragonkin:     [s('dragonspawn'), s('dragonspawn2'), s('drakonid2')],
  dragonspawn:   [s('dragonspawn'), s('dragonspawn2')],
  drakonid:      [s('drakonid2')],
  dragon:        [s('dragon'), s('dragon2'), s('drake')],
  whelp:         [s('dragonwhelp'), s('dragonwhelp2'), s('dragonwhelp3')],
  drake:         [s('drake'), s('drakeadon')],
  hydra:         [s('hydra'), s('hydraoutland'), s('thunderhydra')],
  faerie_dragon: [s('faeriedragon'), s('faeriedragon2')],
  basilisk:      [s('basilisk'), s('basilisk2'), s('basiliskoutland')],
  chimera:       [s('chimera'), s('chimera2'), s('chimerabeast')],

  // --- Mechanicals ---
  mechanical:    [s('shredder'), s('mechanicalspider'), s('mechanowolf')],
  golem:         [s('golemiron'), s('golemstone'), s('mountaingiant')],
  shredder:      [s('shredder'), s('rocketshredder9001')],

  // --- Misc ---
  yeti:          [s('yeti')],
  gargoyle:      [s('gargoyle'), s('gargoyle2'), s('gargoylebrute')],
  ent:           [s('ent'), s('ent2'), s('treefolk')],
  treant:        [s('treefolk'), s('ent')],
  giant:         [s('seagiant'), s('mountaingiant'), s('northrendgiant')],
  yeti_large:    [s('yeti')],

  // --- Iconic WotLK bosses ---
  lichking:      [s('arthaslichking'), s('arthas'), s('arthasundead')],
  arthas:        [s('arthas'), s('arthaslichking'), s('arthasundead')],
  illidan:       [s('illidan'), s('illidandark2'), s('abyssalillidan')],
  kelthuzad:     [s('kelthuzad'), s('kelthuzadshadowlands')],
  yoggsaron:     [s('yoggsaron')],
  kiljaeden:     [s('kiljaeden')],
  alexstrasza:   [s('alexstrasza')],
  malygos:       [s('malygos')],
  nozdormu:      [s('nozdormu')],
  ragnaros:      [s('ragnaros'), s('babyragnoros')],
  hakkar:        [s('hakkar'), s('hakkarshadowlands')],
  kelp:          [s('kelpelemental')],
  jaina:         [s('jaina'), s('jaina3')],
  varian:        [s('kingvarianwrynn')],
  sylvanas:      [s('ladysylvanaswindrunner')],
  vashj:         [s('ladyvashj')],

  // --- World boss / generic boss fallback ---
  'world-boss':  [s('alexstrasza'), s('ragnaros'), s('hakkar'), s('arthaslichking')],
  boss:          [s('arthaslichking'), s('illidan'), s('ragnaros')],
  'boss-enraged':[s('arthaslichking'), s('illidan'), s('ragnaros')],
};

/** R232q43: zone prefixes that may be prepended to a kind by zone-spawns
 *  (e.g. "redrock_goblin" → strip → "goblin"). Sourced from current
 *  Russian-fantasy zone names + their slug equivalents.
 */
const ZONE_PREFIXES = [
  'redrock', 'tirisfal', 'lochmod', 'wilds', 'silverpine',
  'stoneclaw', 'duskhollow', 'mireland', 'needlespires',
  'dustmire', 'frostspire', 'sunpeak', 'oakhold', 'ashvale',
  'goldcoast', 'thornvale', 'ironpeak', 'silverbough',
  'wintergrasp', 'bronzemarsh',
];

/**
 * R232q43: Resolve mob.kind → Sirus path candidates.
 *
 * Resolution order:
 *   1. Exact direct lookup.
 *   2. Strip leading zone prefix ("redrock_goblin" → "goblin"), retry.
 *   3. Lowercase + strip dashes/underscores, retry.
 *   4. Token-based fuzzy: try every space/underscore-separated token
 *      against the table (e.g. "felfire_demon" → "demon").
 *
 * Returns null when no candidate is found, signalling the caller should
 * fall back to its own path (Quaternius/Sketchfab).
 */
export function lookupSirusCreaturePath(kind: string): string[] | null {
  if (!kind) return null;
  const direct = SIRUS_CREATURE_PATHS[kind];
  if (direct) return direct;

  const lower = kind.toLowerCase();
  if (lower !== kind) {
    const lc = SIRUS_CREATURE_PATHS[lower];
    if (lc) return lc;
  }

  // Strip zone prefix.
  for (const p of ZONE_PREFIXES) {
    if (lower.startsWith(`${p}_`)) {
      const stripped = lower.slice(p.length + 1);
      const hit = SIRUS_CREATURE_PATHS[stripped];
      if (hit) return hit;
    }
  }

  // Strip dashes/underscores entirely.
  const flat = lower.replace(/[-_\s]+/g, '');
  if (flat !== lower) {
    const f = SIRUS_CREATURE_PATHS[flat];
    if (f) return f;
  }

  // Token-based fuzzy: split on _ - space, try each token.
  const tokens = lower.split(/[-_\s]+/).filter(Boolean);
  for (const tok of tokens) {
    const hit = SIRUS_CREATURE_PATHS[tok];
    if (hit) return hit;
  }

  return null;
}
